<template>
<div>
	<p class="text-xs font-bold text-base-content flex items-center mb-4" v-if="header">
		<PaperClipIcon class="w-4 h-4 mr-1" />
		{{ $t('Attachments' )}}

		<label :for="'input-' + inputId" class="cursor-pointer ml-4" @click.prevent="openFileDialog()">
			<span class="btn btn-primary btn-circle btn-xs">
				<PlusIcon class="w-4 h-4"></PlusIcon>
			</span>
		</label>
	</p>

	<template v-if="!readOnly">
		<!-- gallery input -->
		<sms-file-input ref="fileInput" class="hidden" :id="'input-' + inputId" :accept="accept" @selectFile="selectFile" :clearAfterSelect="true" />

		<!-- camera input -->
		<sms-file-input ref="cameraInput" class="hidden" :id="'camera-' + inputId" accept="image/*" capture="environment" @selectFile="selectFile" :clearAfterSelect="true" />

		<div class="flex items-center justify-center gap-2" :class="[externalFilesButton || uploadButton? 'mb-6' : '']">
			<GoogleDrivePicker v-if="externalFilesButton && usersStore.me.hasCapability('google-drive')" @filesSelected="selectExternalFiles" ref="googleFilePicker" class="btn btn-outline" :label="$t('Pick from Google Drive')" :accepts="accepts" />
			<OneDrivePicker v-if="externalFilesButton && usersStore.me.hasCapability('onedrive')" @filesSelected="selectExternalFiles" ref="oneDriveFilePicker" class="btn btn-outline" :label="$t('Pick from OneDrive')" :accepts="accepts" />

			<button v-if="uploadButton" type="button" class="btn btn-outline" @click="openFileDialog()">
				<DocumentArrowUpIcon class="w-5 h-5" />
				<span class="hidden sm:inline-block">{{ $t('Upload file') }}</span>
			</button>
		</div>
	</template>

	<div v-if="!addPhotoButton && !attachmentsLocalCopy.length && !uploads.length" class="text-base-content-light text-sm text-center italic">
		<div>{{ placeholderText ?? $t('No attachments added yet') }}</div>
	</div>

	<template v-else>

		<!-- Preview mode -->
		<template v-if="preview">
			<div class="flex gap-2 flex-wrap">
				<template v-if="addPhotoButton">
					<!-- Touch devices without a native camera option: let the user choose between camera and gallery -->
					<Dropdown v-if="offerCameraChoice" position="bottom-start" wrapperClasses="relative shrink-0 text-left">
						<template #trigger>
							<AttachmentsAddPhotoButton />
						</template>
						<template #content="{ close }">
							<DropdownMenu class="w-48">
								<DropdownMenuItem>
									<a @click="openCamera(); close()">
										<CameraIcon class="w-5 h-5 mr-2" />
										{{ $t('Take picture') }}
									</a>
								</DropdownMenuItem>
								<DropdownMenuItem>
									<a @click="openGallery(); close()">
										<PhotoIcon class="w-5 h-5 mr-2" />
										{{ $t('Upload picture') }}
									</a>
								</DropdownMenuItem>
							</DropdownMenu>
						</template>
					</Dropdown>

					<!-- Everywhere else the native file dialog does the job -->
					<AttachmentsAddPhotoButton v-else @click="openGallery()" />
				</template>

				<template  v-for="file in attachmentsLocalCopy">

					<!-- Image -->
					<div v-if="file.type === 'image'" class="w-24 h-24 p-1 bg-base rounded border border-gray-300 relative">
						<button type="button" class="absolute bottom-0 right-0 btn btn-ghost btn-circle btn-sm bg-base hover:bg-danger hover:text-white" @click="removeAttachment(file)">
							<TrashIcon class="w-4 h-4" />
						</button>
						<FilePreview :file="file" data-fancybox="gallery" class="block w-full h-full" />
					</div>

					<!-- Something else -->
					<div v-else >
						<AttachmentsUploadedFile :file="file" @remove="removeAttachment(file)" />
					</div>

				</template>
			</div>

			<div v-if="uploads.length" class="flex flex-col gap-1 mt-3">
				<AttachmentsUploadingFile v-for="file in uploads" :upload="file" :validationError="file.errors" @cancel="cancelUpload(file)" />
			</div>

		</template>

		<!-- List mode -->
		<div v-else class="flex flex-col gap-2">

			<AttachmentsUploadingFile v-for="file in uploads" :upload="file" :validationError="file.errors" @cancel="cancelUpload(file)" />
			<AttachmentsUploadedFile :file="file" @remove="removeAttachment(file)" v-for="file in attachmentsLocalCopy" :readOnly="readOnly" />

		</div>


	</template>

	<p v-if="validationError" class="text-red-600 text-xs">
        {{ validationError }}
    </p>

</div>
</template>

<script lang="ts">

import { mapStores } from "pinia";
import { useUploadStore } from "@/stores/Upload.store";
import AttachmentsUploadingFile from "@/components/AttachmentsUploadingFile.vue";
import AttachmentsUploadedFile from "@/components/AttachmentsUploadedFile.vue";
import AttachmentsAddPhotoButton from "@/components/AttachmentsAddPhotoButton.vue";
import { PaperClipIcon, PlusIcon, TrashIcon, DocumentArrowUpIcon, CameraIcon, PhotoIcon } from "@heroicons/vue/24/outline";
import FilePreview from "@/components/FilePreview.vue";
import GoogleDrivePicker from "@/components/GoogleDrivePicker.vue";
import {useUsersStore} from "@/stores/Users.store";
import OneDrivePicker from "@/components/OneDrivePicker.vue";
import {FileExternal} from "@/models/FileExternal.model";
import { needsCameraChoice } from "@/utils/Device.util";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownMenu from "@/components/ui/DropdownMenu.vue";
import DropdownMenuItem from "@/components/ui/DropdownMenuItem.vue";


export default {
	components: {
		OneDrivePicker,
		FilePreview,
		AttachmentsUploadedFile,
		AttachmentsUploadingFile,
		AttachmentsAddPhotoButton,
		PaperClipIcon, PlusIcon, TrashIcon, DocumentArrowUpIcon, CameraIcon, PhotoIcon,
		GoogleDrivePicker,
		Dropdown, DropdownMenu, DropdownMenuItem
	},

	props: {
		modelValue: {
			type: Array
		},
		preview: {
			type: Boolean,
			default: false
		},
		accept: {
			type: String,
			default: '*'
		},
		uploading: {
			type: Boolean,
			default: false
		},
		header: {
			type: Boolean,
			default: false
		},
		uploadButton: {
			type: Boolean,
			default: false
		},
		externalFilesButton: {
			type: Boolean,
			default: false
		},
		readOnly: {
			type: Boolean,
			default: false
		},
		placeholderText: {
			type: String,
		},
		addPhotoButton: {
			type: Boolean,
			default: false
		},

		/**
		 * Controls which entity types can be selected.
		 * Accepted values: 'files', 'folders' or both.
		 * Defaults to ['files'] (only files allowed).
		 *
		 * @example :accepts="['files']"            // files only (default)
		 * @example :accepts="['folders']"         // folders only
		 * @example :accepts="['files', 'folders']" // both
		 */
		accepts: {
			type: Array as () => Array<'files' | 'folders'>,
			default: () => ['files'],
			validator: (value: string[]) => value.every(v => ['files', 'folders'].includes(v)),
		},
	},

	computed: {
		...mapStores(useUploadStore, useUsersStore),
	},

	data() {
		return {
			uploads: [],
			attachmentsLocalCopy: [],
			inputId: null,
			validationError: null,
			uploadingFiles: 0,
			offerCameraChoice: needsCameraChoice(),
		}
	},

	mounted() {
		this.inputId = Math.floor(Math.random() * 1000000);
		this.attachmentsLocalCopy = [ ... this.modelValue ].sort((a, b) => b.created_at.localeCompare(a.created_at));
	},

	methods: {
		selectFile(file) {
			if (!file) {
				return;
			}

			this.uploadingFiles ++;
			this.emitUploadingStatus();

			let uploadingFile = this.uploadStore.upload(file, (file) => {

				this.uploadingFiles --;
				this.emitUploadingStatus();

				this.attachmentsLocalCopy.unshift(file);
				this.uploads = this.uploads.filter(f => f !== uploadingFile);

				this.$emit('update:modelValue', this.attachmentsLocalCopy);
			}, (error) => {

				this.uploadingFiles --;
				this.emitUploadingStatus();

			});

			this.uploads.unshift(uploadingFile);
		},

		emitUploadingStatus() {
			this.$emit('update:uploading', this.uploadingFiles > 0);
		},

		filesDropped(files) {
			files.forEach(file => {
				this.selectFile(file);
			});
		},

		cancelUpload(file) {
			this.uploads = this.uploads.filter(f => f !== file);
		},

		removeAttachment(file) {
			this.attachmentsLocalCopy = this.attachmentsLocalCopy.filter(f => f !== file);
			this.$emit('update:modelValue', this.attachmentsLocalCopy);
		},

		openFileDialog() {
			this.$refs.fileInput.openFileDialog();
		},

		openCamera() {
			this.$refs.cameraInput.openFileDialog();
		},

		openGallery() {
			this.$refs.fileInput.openFileDialog();
		},

		selectExternalFiles(files: FileExternal[]) {
			files.forEach(file => {
				this.selectExternalFile(file);
			});
		},

		async selectExternalFile(file: FileExternal) {

			this.uploadingFiles ++;
			this.emitUploadingStatus();

			try {
				let uploadedFile = await this.uploadStore.addFromExternalStorage(file);

				this.uploadingFiles--;
				this.emitUploadingStatus();

				this.attachmentsLocalCopy.unshift(uploadedFile);
				this.$emit('update:modelValue', this.attachmentsLocalCopy);

			} catch (e) {
				this.uploadingFiles--;
				this.emitUploadingStatus();

				console.error(e);
				this.$notify({ text: this.$t('Could not add the file {name}', { name: file.name }), type: 'error' });
			}

		}

	}

}

</script>
